import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import RichTextEditor from "../../../../../common/RichTextEditor";
import { mainLayoutStyles, showNotification } from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import {
  QC_DEFAULT_TEMPLATE_ID,
  useGetQCTemplateById,
  useSaveQCTemplate,
} from "../../../../../modules/OrganizationQCTemplates/hooks/QCTemplatesHooks";
import { QC_GREETING_TOKENS } from "../../../../../modules/OrganizationQCTemplates/hooks/qcGreeting";

/**
 * General Admin: view / edit the shared `default` QuickConnect template.
 *
 * This is the seed template every organization inherits until it saves its own
 * copy, so General Admin edits it IN PLACE (`editInPlace`) rather than forking
 * it the way an org admin would. General Admin only; there is no list page.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */
const GAQCTemplateContainer = () => {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const { template, loading: fetchLoading, refetch } = useGetQCTemplateById(
    QC_DEFAULT_TEMPLATE_ID
  );
  const { saveTemplate, loading: saveLoading } = useSaveQCTemplate();

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({ name: "", greeting: "" });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        greeting: template.greeting || "",
      });
    }
  }, [template]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showNotification("Please enter a template name");
      return;
    }

    const result = await saveTemplate({
      templateId: QC_DEFAULT_TEMPLATE_ID,
      isDefault: true,
      editInPlace: true,
      formData,
      userId,
    });

    if (!result.success) {
      showNotification("Error saving template");
      return;
    }

    setIsEditMode(false);
    showNotification("Default QuickCollect template updated");
    refetch();
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (template) {
      setFormData({
        name: template.name || "",
        greeting: template.greeting || "",
      });
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={"QuickCollect Template"}
        bgColor="#fff"
        buttonText={isEditMode ? undefined : "Edit"}
        editEnabled={isEditMode}
        setEditEnabled={setIsEditMode}
        submitButtonText={"Save"}
        cancelButton={isEditMode}
        onCancelClick={handleCancel}
        handleSave={handleSave}
        loading={saveLoading}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction={"column"}
          style={{ padding: 30, marginTop: 40 }}
        >
          <Paper sx={{ p: 3, maxWidth: 900 }}>
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#E3F2FD",
                border: "1px solid #90CAF9",
              }}
            >
              <Typography sx={{ ...labelStyles, color: "#0D47A1", mb: 0 }}>
                This is the default QuickCollect template. It is used by every
                organization that has not saved its own copy.
              </Typography>
            </Box>

            {/* Name */}
            <Typography sx={labelStyles}>Template Name</Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.name}
              disabled={!isEditMode}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ mb: 3, maxWidth: 500 }}
            />

            {/* Greeting */}
            <Typography sx={labelStyles}>Greeting</Typography>
            <Typography sx={helperStyles2}>
              Shown at the top of the QuickCollect submission page. Available
              tokens: {QC_GREETING_TOKENS.join(", ")}
            </Typography>
            {/* Same component in both modes so view and edit render
                identically; disabled just hides the toolbar. */}
            <RichTextEditor
              value={formData.greeting}
              disabled={!isEditMode}
              onChange={(html) =>
                setFormData((prev) => ({ ...prev, greeting: html }))
              }
            />
          </Paper>
        </Grid>
      </Grid>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={fetchLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default GAQCTemplateContainer;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mb: 1,
};

const helperStyles2 = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 13,
  color: "#808080",
  mb: 1,
};

